import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SiteLogo } from '../../components/ui/SiteLogo';
import { Skeleton } from '../../components/ui/Skeleton';
import { MessageMarkdown } from '../../features/chat/MessageMarkdown';
import { ChatMessageAttachments } from '../../features/chat/ChatMessageAttachments';
import { QuotedMessagePreview } from '../../features/chat/QuoteBar';
import { useI18n } from '../../hooks/useI18n';
import { fetchSharedConversationApi, SHARE_ERROR_CODES, SharedConversationError } from '../../api/share.api';
import { SharePageSeo } from './SharePageSeo';
import { buildLocalizedPath } from '../../seo/urls';

const isSafeHttpUrl = (url) => /^https?:\/\//i.test(String(url || ''));

const errorCopyKey = (code) => {
  switch (code) {
    case SHARE_ERROR_CODES.revoked:
      return 'sharedConversationRevoked';
    case SHARE_ERROR_CODES.deleted:
      return 'sharedConversationDeleted';
    case SHARE_ERROR_CODES.expired:
      return 'sharedConversationExpired';
    default:
      return 'sharedConversationInvalid';
  }
};

const SafeImageGrid = ({ urls, className }) => {
  const safeUrls = (urls || []).filter(isSafeHttpUrl);
  if (!safeUrls.length) {
    return null;
  }

  return (
    <div className={className}>
      {safeUrls.map((url) => (
        <a key={url} href={url} target="_blank" rel="noopener noreferrer">
          <img src={url} alt="" className="w-full rounded-xl object-cover" loading="lazy" />
        </a>
      ))}
    </div>
  );
};

const SharedMessage = ({ message }) => {
  const { t } = useI18n();
  const isUser = message.role === 'user';
  const text = message.content || message.message || '';

  return (
    <article className={isUser ? 'flex flex-col items-start' : 'flex w-full flex-col items-start'}>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
        {isUser ? t('sharedConversationUser') : t('sharedConversationAssistant')}
      </p>
      {message.quotedText ? <QuotedMessagePreview text={message.quotedText} className="self-start text-start" /> : null}
      {isUser ? (
        <div className="max-w-[85%] rounded-3xl bg-neutral-100 px-4 py-3 text-base leading-8 text-neutral-900 dark:bg-neutral-900 dark:text-neutral-100">
          <ChatMessageAttachments urls={message.imageUrls} className="mb-2" />
          <div className="whitespace-pre-wrap">{text}</div>
        </div>
      ) : (
        <div className="w-full px-1 py-1 text-base leading-8 text-neutral-900 dark:text-neutral-100">
          <ChatMessageAttachments urls={message.imageUrls} className="mb-2" />
          {text ? <MessageMarkdown content={text} /> : null}
          <SafeImageGrid urls={message.generatedImageUrls} className="mt-3 grid gap-2 sm:grid-cols-2" />
        </div>
      )}
    </article>
  );
};

export const SharedConversationPage = () => {
  const { token } = useParams();
  const { t, language } = useI18n();
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorCode(null);
    setConversation(null);

    fetchSharedConversationApi(token)
      .then((result) => {
        if (!cancelled) {
          setConversation(result);
        }
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setErrorCode(error instanceof SharedConversationError ? error.code : SHARE_ERROR_CODES.invalid);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const canonical = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-app text-text">
      <SharePageSeo title={conversation?.title} canonical={canonical} />
      <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-4 py-6 sm:px-6">
        <header className="mb-8 flex items-center gap-3">
          <Link to={buildLocalizedPath('/', language)} className="flex min-w-0 items-center gap-3">
            <SiteLogo />
            <div className="text-start">
              <p className="text-sm font-bold tracking-tight">{t('landingBrand')}</p>
              <p className="text-xs text-muted">{t('landingTopTag')}</p>
            </div>
          </Link>
        </header>

        {loading ? (
          <div aria-busy="true" aria-live="polite" className="space-y-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : errorCode ? (
          <section className="flex flex-1 flex-col items-center justify-center py-16 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">{t(errorCopyKey(errorCode))}</h1>
            <Link
              to={buildLocalizedPath('/', language)}
              className="mt-6 inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 dark:text-slate-950"
            >
              {t('sharedConversationTry')}
            </Link>
          </section>
        ) : (
          <main className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">{t('sharedConversationBadge')}</p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-neutral-950 dark:text-white">
              {conversation.title || t('shareConversationUntitled')}
            </h1>
            <div className="mt-8 space-y-6">
              {(conversation.messages || []).map((message) => (
                <SharedMessage key={message.id} message={message} />
              ))}
            </div>
          </main>
        )}

        <footer className="mt-12 border-t border-border py-8 text-center">
          <p className="text-sm font-medium text-text">{t('sharedConversationCreatedWith')}</p>
          <Link
            to={buildLocalizedPath('/', language)}
            className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-5 text-sm font-bold text-white shadow-sm transition hover:opacity-95 dark:text-slate-950"
          >
            {t('sharedConversationTry')}
          </Link>
        </footer>
      </div>
    </div>
  );
};
